import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DetectionDto, RaceAdminSummaryDto, RegExpEnum, RunnerAdminDto, RunnerStatusEnum, runnerStatusLabel, TeamDto } from "@aoe/client";
import { AngularEditorModule } from '@kolkov/angular-editor';
import * as _ from 'lodash-es';
import { DialogService } from 'src/app/core/services/dialog.service';

import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule } from '@angular/common';
import { MatAnchor, MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { Store } from '@ngrx/store';
import { filter, Observable, take, tap } from 'rxjs';
import { selectEventIdAdminSelected } from 'src/app/core/store/admin/event-admin/event-admin.selector';
import { selectRaceAdminList } from 'src/app/core/store/admin/race-admin/raceAdmin.selector';
import { enteriesActions } from 'src/app/core/store/enteries/enteries.actions';
import { selectEnteryById } from 'src/app/core/store/enteries/enteries.selector';
import { IconsEnum } from 'src/app/shared/enum/Icons.enum';
import { PassagesRecapModule } from 'src/components/passages-recap/passages-recap.module';

@Component({
  selector: 'aoe-runner-admin',
  templateUrl: './runner-admin.component.html',
  styleUrls: ['./runner-admin.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatTabsModule,
    ClipboardModule,
    PassagesRecapModule,
    AngularEditorModule,
    MatIconModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatAnchor,
    MatButtonModule
  ]
})
export class RunnerAdminComponent implements OnInit, OnDestroy {

  passage!: DetectionDto[];
  runnerFg!: FormGroup;
  teamForm!: FormGroup;
  teamList!: Array<TeamDto>;
  eventId!: string | null;
  races$!: Observable<RaceAdminSummaryDto[]>

  icons = IconsEnum;
  bibChip = false;

  runnerStatusLabel = runnerStatusLabel;
  runnerStatus = Object.values(RunnerStatusEnum);

  constructor(
    @Inject(MAT_DIALOG_DATA) private readonly data: { runner?: RunnerAdminDto },
    private readonly dialogService: DialogService,
    private readonly store: Store,
    private readonly fb: FormBuilder
  ) {
  }

  ngOnInit(): void {

    this.initAndSetForm(null);

    this.store.select(selectEventIdAdminSelected).pipe(
      filter(id => !!id),
      take(1),
      tap(id => {
        if (id && this.data.runner?.id)
          this.store.dispatch(enteriesActions.loadEnteryById({ eventId: id, payload: { id: this.data.runner?.id } }))
      })
    ).subscribe(id => this.eventId = id);

    this.store.select(selectEnteryById).pipe(filter(e => !!e), take(1)).subscribe(r => this.initAndSetForm(r))

    this.races$ = this.store.select(selectRaceAdminList);

    this.runnerFg.valueChanges.subscribe(() => console.log(this.runnerFg))

  }

  ngOnDestroy(): void {
    this.store.dispatch(enteriesActions.resetSelectedRunner())
  }

  private initAndSetForm(runner: RunnerAdminDto | null): void {
    this.runnerFg = this.fb.group({
      name: [
        runner?.lastName ?? null,
        [Validators.required, Validators.minLength(3)]
      ],
      firstName: [
        runner?.firstName ?? null,
        [Validators.required, Validators.minLength(3)]
      ],
      birthdate: [
        runner?.birthdate ?? null,
        [Validators.required]
      ],
      category: [runner?.category?.event ?? null],
      category_federal: [runner?.category?.federal ?? null],
      town: [
        runner?.city ?? null,
        [Validators.minLength(3)]
      ],
      club: [
        runner?.club ?? null,
        [Validators.minLength(3)]
      ],
      country: [
        runner?.country ?? 'FRA',
        [Validators.minLength(3)]
      ],
      sex: [
        runner?.sex ?? 'H',
        [Validators.required]
      ],
      nickname: [
        runner?.nickname ?? null,
        [Validators.minLength(3)]
      ],
      info: [
        runner?.info ?? null,
        [Validators.minLength(3)]
      ],
      mail: [
        runner?.mail ?? null,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.pattern(RegExpEnum.MAIL)
        ]
      ],
      bib: [
        runner?.bib ?? null,
        [Validators.required, Validators.pattern('^[0-9]+$')]
      ],
      chip: [
        runner?.chip ?? null,
        [Validators.pattern('^[0-9]+$')]
      ],
      status: [
        runner?.status ?? RunnerStatusEnum.NOT_STARTED,
        [Validators.required]
      ],
      raceId: [runner?.race?.id ?? null]
    });
  }


  get isOnTeamRaceSelected() {
    return true;
  }

  get runnerName() {
    return this.runnerFg.get('lastname')?.value
  }

  get runnerFirstName() {
    return this.runnerFg.get('firstNane')?.value
  }

  get bib() {
    return this.runnerFg.get('bib')?.value
  }

  get country() {
    return this.runnerFg.get('country')?.value
  }

  get nickname() {
    return this.runnerFg.get('nickname')?.value
  }

  get club() {
    return this.runnerFg.get('club')?.value
  }

  get countryFlagUrl() {
    if (this.country)
      return `https://flagcdn.com/${this.country.slice(0, 2).toLowerCase()}.svg`;
    return '';
  }

  get isFormInitialized(): boolean {
    return !_.isNil(this.runnerFg);
  }

  get id(): string | null {
    return this.data.runner?.id ?? null;
  }
  get runner() {
    return this.data.runner;
  }

  close(): void {
    this.dialogService.closeRunnerAdminModal();
  }

  submit(): void {
    if (this.eventId) {
      if (!this.bibChip && this.runnerFg.get('bib')?.value)
        this.runnerFg.get('chip')?.setValue(this.runnerFg.get('bib')?.value)
      if (this.data.runner?.id) {
        this.store.dispatch(
          enteriesActions.updateEnteries({
            eventId: this.eventId,
            id: this.data.runner?.id,
            payload: this.runnerFg.value
          }))
      } else {
        this.store.dispatch(
          enteriesActions.createEnteries({ payload: this.runnerFg.value, eventId: this.eventId }))
      }
    }

  }

  submitTeam(): void {
  }

}
